import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import {
    BulletIcon,
    Button,
    ButtonGroup,
    Container,
    ErrorText,
    Header,
    InstructionsBox,
    InstructionsTitle,
    InstructionText,
    MachineCodeInput,
    MachineCodeLabel,
    MachineCodeSection,
    Overlay,
    InstructionRow,
    InfoIcon,
    WarningBox,
} from "./style";
import { ApplicationContext } from "../../../components/ApplicationProvider";
import BulletIconImage from "../../../images/cognito_auth/bullet.svg";
import InfoIconImage from "../../../images/info.svg";
import WarnIconImage from "../../../images/warning.svg";
import { HAS_EXCEEDED_USER_LICENSE_ACCESS_LIMIT } from "../../../graphql/Mutations/UserLicenseAccessLimit";
import Parser from "html-react-parser";

interface OfflineLicensingModalProps {
    isOpen: boolean;
    machineCode: string;
    error?: string;
    onMachineCodeChange: (value: string) => void;
    onConfirm: () => void;
    onCancel: () => void;
}

function formatMachineCode(value: string): string {
    const cleaned = value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 9);
    return cleaned.match(/.{1,3}/g)?.join("-") ?? "";
}

const OfflineLicensingModal: React.FC<OfflineLicensingModalProps> = ({
    isOpen,
    machineCode,
    error,
    onMachineCodeChange,
    onConfirm,
    onCancel,
}) => {
    const { t } = useContext(ApplicationContext);
    const stratasysSupportLink = `https://support.stratasys.com/en`;

    const [hasExceededUserLicenseAccessLimit] = useMutation(HAS_EXCEEDED_USER_LICENSE_ACCESS_LIMIT);

    const [showWarningBox, setShowWarningBox] = useState(false);

    useEffect(() => {
        async function checkUserLicenseAccessLimit() {
            if (!isOpen) {
                setShowWarningBox(false);
                return;
            }

            setShowWarningBox(false);

            try {
                const response = await hasExceededUserLicenseAccessLimit();

                const hasExceededLimit = response.data?.hasExceededUserLicenseAccessLimit ?? false;

                setShowWarningBox(hasExceededLimit);
            } catch (err) {
                console.error("Failed to check user license access limit", err);

                setShowWarningBox(false);
            }
        }

        void checkUserLicenseAccessLimit();
    }, [isOpen, hasExceededUserLicenseAccessLimit]);

    if (!isOpen) {
        return null;
    }

    const isConfirmDisabled = !!error || !machineCode;

    return (
        <Overlay onClick={onCancel} data-testid="download-license-file-modal">
            <Container onClick={(e) => e.stopPropagation()}>
                <InstructionRow>
                    <InfoIcon src={InfoIconImage} alt="info" data-testid="bullet1" />
                    <Header>
                        {t("licenses_overview.download_license_file_instructions.title")}
                    </Header>
                </InstructionRow>

                <InstructionsBox>
                    <InstructionText>
                        {t("licenses_overview.download_license_file_instructions.instruction1")}
                    </InstructionText>

                    <InstructionsTitle>
                        {t("licenses_overview.download_license_file_instructions.instruction")}
                    </InstructionsTitle>

                    <InstructionRow>
                        <BulletIcon src={BulletIconImage} alt="bullet" data-testid="bullet1" />
                        <InstructionText>
                            {t("licenses_overview.download_license_file_instructions.instruction2")}
                        </InstructionText>
                    </InstructionRow>

                    <InstructionRow>
                        <BulletIcon src={BulletIconImage} alt="bullet" data-testid="bullet1" />
                        <InstructionText>
                            {t("licenses_overview.download_license_file_instructions.instruction3")}
                        </InstructionText>
                    </InstructionRow>

                    <InstructionRow>
                        <BulletIcon src={BulletIconImage} alt="bullet" data-testid="bullet1" />
                        <InstructionText>
                            {t("licenses_overview.download_license_file_instructions.instruction4")}
                        </InstructionText>
                    </InstructionRow>

                    <InstructionRow>
                        <BulletIcon src={BulletIconImage} alt="bullet" data-testid="bullet1" />
                        <InstructionText>
                            {t("licenses_overview.download_license_file_instructions.instruction5")}
                        </InstructionText>
                    </InstructionRow>

                    <InstructionRow>
                        <BulletIcon src={BulletIconImage} alt="bullet" data-testid="bullet1" />
                        <InstructionText>
                            {t("licenses_overview.download_license_file_instructions.instruction6")}
                        </InstructionText>
                    </InstructionRow>
                </InstructionsBox>

                {showWarningBox && (
                    <WarningBox>
                        <InstructionRow>
                            <InfoIcon src={WarnIconImage} alt="warning" data-testid="bullet1" />
                            <Header>
                                {t(
                                    "licenses_overview.download_license_file_instructions.license_detected"
                                )}
                            </Header>
                        </InstructionRow>

                        <InstructionText>
                            {Parser(
                                t("licenses_overview.download_license_file_instructions.warning", {
                                    stratasys_support_link: stratasysSupportLink,
                                })
                            )}
                        </InstructionText>
                    </WarningBox>
                )}

                <MachineCodeSection>
                    <MachineCodeLabel htmlFor="machineCode">
                        {t("licenses_overview.download_license_file_instructions.machine_code")}
                    </MachineCodeLabel>

                    <MachineCodeInput
                        id="machineCode"
                        data-testid="machineCode-input"
                        type="text"
                        maxLength={11}
                        value={formatMachineCode(machineCode)}
                        $hasError={!!error}
                        onChange={(e) => onMachineCodeChange(formatMachineCode(e.target.value))}
                    />

                    {error && <ErrorText>{error}</ErrorText>}
                </MachineCodeSection>

                <ButtonGroup>
                    <Button type="button" $variant="secondary" onClick={onCancel}>
                        {t("licenses_overview.download_license_file_instructions.cancel")}
                    </Button>

                    <Button
                        type="button"
                        $variant="primary"
                        disabled={isConfirmDisabled}
                        onClick={onConfirm}
                        data-testid="confirm-button"
                    >
                        {t("licenses_overview.download_license_file_instructions.confirm")}
                    </Button>
                </ButtonGroup>
            </Container>
        </Overlay>
    );
};

export default OfflineLicensingModal;
